import React from 'react';
import { FadeUp } from './ui/FadeUp';
import { LinkedinIcon } from 'lucide-react';
interface FounderPageProps {
  onNavigate: (view: any) => void;
}
export function FounderPage({
  onNavigate
}: FounderPageProps) {
  return <div className="pt-32 pb-28 bg-white min-h-screen" data-id="element-72">
      <div className="max-w-[1240px] mx-auto px-8" data-id="element-73">
        {/* SECTION 1 — Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-28" data-id="element-74">
          <FadeUp data-id="element-75">
            <div data-id="element-76">
              <p className="text-sm font-medium text-purple-500 mb-4 tracking-wide uppercase" data-id="element-77">
                Meet the Founder
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-78">
                The Operator Behind SparkHR
              </h1>
              <div className="mb-6" data-id="element-79">
                <p className="text-xl font-semibold text-gray-900" data-id="element-80">
                  Krystina Milakovic
                </p>
                <p className="text-base text-[#9CA3AF]" data-id="element-81">Founder, SparkHR</p>
              </div>
              <p className="text-lg text-purple-500 font-medium mb-8" data-id="element-82">
                People Operations operator focused on building scalable HR
                infrastructure for growing companies.
              </p>
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed mb-8" data-id="element-83">
                <p data-id="element-84">
                  Krystina Milakovic is the founder of SparkHR, a modern People
                  Operations firm helping growing companies build structured HR
                  infrastructure, systems, and operational clarity.
                </p>
                <p data-id="element-85">
                  After years leading HR and operations inside growing
                  companies, Krystina repeatedly saw the same problem: founders
                  and leadership teams struggling to manage people operations
                  without the infrastructure to support it.
                </p>
                <p data-id="element-86">SparkHR was built to solve that gap.</p>
                <p data-id="element-87">
                  Today, SparkHR helps organizations structure their People
                  Operations through embedded HR specialists, operational
                  playbooks, and scalable systems that keep HR running smoothly
                  as companies grow.
                </p>
              </div>
              <a href="https://www.linkedin.com/in/krystinalea/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#0A66C2] hover:text-[#004182] font-medium transition-colors" data-id="element-88">
                <LinkedinIcon className="w-5 h-5" data-id="element-89" />
                Connect with Krystina on LinkedIn
              </a>
            </div>
          </FadeUp>

          <FadeUp delay={0.2} data-id="element-90">
            <div className="flex justify-center lg:justify-end" data-id="element-91">
              <div className="w-full max-w-md" data-id="element-92">
                <img src="https://cdn.magicpatterns.com/uploads/4RaKNLM2a4LTWU6sgDcurS/1770058097830.jpg" alt="Krystina Milakovic, Founder of SparkHR" className="w-full aspect-[4/5] object-cover object-top rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.1)]" data-id="element-93" />
              </div>
            </div>
          </FadeUp>
        </div>

        {/* SECTION 2 — Experience */}
        <FadeUp data-id="element-94">
          <div className="max-w-3xl mb-28" data-id="element-95">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-96">
              Experience in Building HR Infrastructure
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-97">
              Krystina has worked across multiple industries helping companies
              design and operate structured HR functions. Her work focuses on
              operational HR infrastructure including:
            </p>
            <ul className="space-y-3 text-lg text-gray-700 mb-6" data-id="element-98">
              <li className="flex items-start gap-3" data-id="element-99">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2.5 shrink-0" data-id="element-100"></span>
                HR systems and HRIS implementations
              </li>
              <li className="flex items-start gap-3" data-id="element-101">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2.5 shrink-0" data-id="element-102"></span>
                Compliance and documentation frameworks
              </li>
              <li className="flex items-start gap-3" data-id="element-103">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2.5 shrink-0" data-id="element-104"></span>
                Onboarding and employee lifecycle processes
              </li>
              <li className="flex items-start gap-3" data-id="element-105">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2.5 shrink-0" data-id="element-106"></span>
                Manager support and operational HR guidance
              </li>
              <li className="flex items-start gap-3" data-id="element-107">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2.5 shrink-0" data-id="element-108"></span>
                Scalable HR playbooks and workflows
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed" data-id="element-109">
              Her approach focuses on building systems that allow companies to
              manage people operations consistently as they grow.
            </p>
          </div>
        </FadeUp>

        {/* SECTION 3 — Why SparkHR Exists */}
        <FadeUp data-id="element-110">
          <div className="max-w-3xl mb-28" data-id="element-111">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-112">
              Why SparkHR Exists
            </h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-113">
              <p data-id="element-114">
                Many growing companies wait too long to build structured HR
                operations.
              </p>
              <p data-id="element-115">
                Responsibilities often fall to founders, finance teams, or
                operations leaders who are already stretched thin. Over time, HR
                tasks accumulate and become difficult to manage consistently.
              </p>
              <p data-id="element-116">SparkHR was designed as a modern alternative.</p>
              <p data-id="element-117">
                Instead of hiring a full internal HR department too early,
                organizations can access structured People Operations support
                through SparkHR's embedded team model.
              </p>
              <p data-id="element-118">
                This allows companies to build operational HR infrastructure
                while maintaining flexibility as they scale.
              </p>
            </div>
          </div>
        </FadeUp>

        {/* SECTION — The Middle Market HR Gap */}
        <FadeUp data-id="element-119">
          <div className="max-w-3xl mb-28" data-id="element-120">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-121">
              The Middle Market HR Gap
            </h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-122">
              <p data-id="element-123">
                Many organizations eventually outgrow ad-hoc HR support but are
                not yet ready to build a full internal HR department.
              </p>
              <p data-id="element-124">
                At this stage, companies often find themselves stuck between two
                options: hiring a full-time HR leader earlier than necessary or
                relying on fragmented consulting support that lacks operational
                consistency.
              </p>
              <p data-id="element-125">
                This gap is especially common in growing companies where HR
                responsibilities continue to expand but internal infrastructure
                has not yet caught up.
              </p>
              <p data-id="element-126">SparkHR was designed to bridge this gap.</p>
              <p data-id="element-127">
                By combining embedded HR specialists with structured operational
                systems, SparkHR allows companies to maintain consistent People
                Operations without the overhead of building a full internal HR
                department too early.
              </p>
              <p data-id="element-128">
                This model helps organizations maintain compliance, support
                managers, and scale their people operations with clarity as the
                business grows.
              </p>
            </div>
          </div>
        </FadeUp>

        {/* SECTION 4 — Looking Ahead */}
        <FadeUp data-id="element-129">
          <div className="max-w-3xl mb-28" data-id="element-130">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-131">
              What's Next for SparkHR
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-10" data-id="element-132">
              In addition to supporting companies directly through SparkHR,
              Krystina is developing educational and operational resources
              designed to strengthen management and people operations.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4" data-id="element-133">
              Upcoming initiatives include:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-134">
              <div className="bg-[#faf6ff] rounded-xl border border-[#eee] p-6" data-id="element-135">
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-136">
                  Manager Masterclass
                </h3>
                <p className="text-gray-600 text-base leading-relaxed" data-id="element-137">
                  Practical training designed to help managers navigate
                  real-world employee situations with clarity and confidence.
                </p>
              </div>
              <div className="bg-[#faf6ff] rounded-xl border border-[#eee] p-6" data-id="element-138">
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-139">
                  People Operations Insights
                </h3>
                <p className="text-gray-600 text-base leading-relaxed" data-id="element-140">
                  Ongoing writing and insights focused on HR infrastructure,
                  operational design, and scaling People Operations in modern
                  companies.
                </p>
              </div>
            </div>
          </div>
        </FadeUp>

        {/* SECTION 5 — CTA */}
        <FadeUp data-id="element-141">
          <div className="bg-[#faf6ff] rounded-xl p-8 border border-[#eee] text-center max-w-3xl" data-id="element-142">
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-143">
              Interested in working with SparkHR?
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-144">
              SparkHR provides structured People Operations support for growing
              companies.
            </p>
            <a href="https://calendly.com/krystina-joinsparkhr/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-white bg-gradient-primary rounded-full hover:shadow-md hover:opacity-90 transition-all" data-id="element-145">
              Book an Intro Call
            </a>
          </div>
        </FadeUp>
      </div>
    </div>;
}